// Public listing projection.
//
// Mirrors the approved release projection served by the public catalog
// (listPublicLocations / getPublicLocation, docs/api-contract.md 3.11) and the
// publication_releases fields in docs/data-model.md 11. Only allowlisted,
// low sensitivity values live here: no address, coordinates, contacts, owner
// intent, pricing or private sources (docs/product-and-commercial.md 3).
//
// Every optional fact distinguishes "unknown" (nobody has confirmed it yet)
// from "not_disclosed" (known privately, withheld from the public release).
//
// These local types are stricter than the generated OpenAPI types (Fact is a
// discriminated union); the assertions at the bottom keep both in step.

import type { components } from '@/api/generated/schema';

export type Disclosure = 'known' | 'unknown' | 'not_disclosed';

export type Fact<T> =
  | { disclosure: 'known'; value: T }
  | { disclosure: 'unknown' }
  | { disclosure: 'not_disclosed' };

export const SITE_CATEGORIES = [
  'operational_datacenter',
  'datacenter_shell',
  'industrial_conversion',
  'powered_land',
  'edge_site',
] as const;
export type SiteCategory = (typeof SITE_CATEGORIES)[number];

// Ordered from smallest to largest. Bands are deliberately coarse.
export const CAPACITY_BANDS = [
  'under_1_mw',
  '1_to_5_mw',
  '5_to_20_mw',
  '20_to_50_mw',
  'over_50_mw',
] as const;
export type CapacityBand = (typeof CAPACITY_BANDS)[number];

// A band never upgrades an estimate into a confirmed claim.
export type CapacityBasis = 'confirmed' | 'estimated';

export const SERVICE_CATEGORIES = [
  'colocation',
  'wholesale_lease',
  'powered_shell',
  'build_to_suit',
  'site_sale',
  'land_sale',
] as const;
export type ServiceCategory = (typeof SERVICE_CATEGORIES)[number];

export const POWER_CATEGORIES = [
  'grid_connected',
  'dedicated_substation',
  'dual_feed',
  'renewable_supply',
  'onsite_generation',
] as const;
export type PowerCategory = (typeof POWER_CATEGORIES)[number];

export const NETWORK_CATEGORIES = [
  'carrier_neutral',
  'multiple_carriers',
  'dark_fibre',
  'internet_exchange_nearby',
  'subsea_landing_nearby',
] as const;
export type NetworkCategory = (typeof NETWORK_CATEGORIES)[number];

export const GRID_CONNECTION_STATUSES = [
  'energised',
  'contracted',
  'application_submitted',
  'not_started',
] as const;
export type GridConnectionStatus = (typeof GRID_CONNECTION_STATUSES)[number];

export const MUNICIPAL_APPROVAL_STATUSES = [
  'granted',
  'in_progress',
  'pre_application',
  'not_required',
  'not_started',
] as const;
export type MunicipalApprovalStatus = (typeof MUNICIPAL_APPROVAL_STATUSES)[number];

export const AVAILABILITY_STATUSES = [
  'available_now',
  'available_from',
  'under_discussion',
  'on_request',
] as const;
export type AvailabilityStatus = (typeof AVAILABILITY_STATUSES)[number];

// Coarse grouping used for filters and the region overview. Derived from
// country_code, never from coordinates.
export const MACRO_REGIONS = [
  'nordics',
  'uk_ireland',
  'benelux',
  'dach',
  'france',
  'iberia',
  'italy',
  'central_eastern',
] as const;
export type MacroRegion = (typeof MACRO_REGIONS)[number];

export interface Availability {
  status: AvailabilityStatus;
  /** Editorial wording, qualified, never a promise. */
  wording: string;
  /** Earliest month (YYYY-MM). Only set when status is available_from. */
  available_from: string | null;
}

export interface Expansion {
  /** Additional capacity band beyond the current capacity. */
  band: CapacityBand;
  basis: CapacityBasis;
}

export interface PublicListing {
  release_id: string;
  public_slug: string;
  title: string;
  summary: string;
  country_code: string;
  region: string;
  coarse_locality: string | null;
  site_category: SiteCategory;
  capacity: Fact<{ band: CapacityBand; basis: CapacityBasis }>;
  expansion: Fact<Expansion>;
  municipal_approval: Fact<MunicipalApprovalStatus>;
  grid_connection: Fact<GridConnectionStatus>;
  power_categories: Fact<PowerCategory[]>;
  network_categories: Fact<NetworkCategory[]>;
  service_categories: ServiceCategory[];
  availability: Availability;
  /** Seeker relevant notes chosen by the editor, short and factual. */
  highlights: string[];
  /** When the facts were last confirmed with the site side. */
  verified_at: string;
  published_at: string;
  /** The release is withdrawn if not reconfirmed by this date. */
  reconfirm_by: string;
}

export type IntroductionRole = 'capacity_seeker' | 'owner_or_operator' | 'adviser' | 'other';

export type IntroductionTiming = 'within_6_months' | '6_to_12_months' | '12_to_24_months' | 'exploring';

export interface IntroductionRequestDraft {
  /** Null for a general requirement not tied to one listing. */
  release_id: string | null;
  requester_name: string;
  requester_email: string;
  organization: string;
  role: IntroductionRole;
  required_capacity_band: CapacityBand | '';
  timing: IntroductionTiming | '';
  purpose: string;
  consent: boolean;
}

// ---------- Contract checks (compile time only) ----------

type Schemas = components['schemas'];
type Same<A, B> = [A] extends [B] ? ([B] extends [A] ? true : false) : false;
type Assert<T extends true> = T;

export type ContractChecks = [
  Assert<PublicListing extends Schemas['PublicLocation'] ? true : false>,
  Assert<Same<SiteCategory, Schemas['PublicSiteCategory']>>,
  Assert<Same<CapacityBand, Schemas['PublicCapacityBand']>>,
  Assert<Same<CapacityBasis, Schemas['PublicCapacityBasis']>>,
  Assert<Same<ServiceCategory, Schemas['PublicServiceCategory']>>,
  Assert<Same<PowerCategory, Schemas['PublicPowerCategory']>>,
  Assert<Same<NetworkCategory, Schemas['PublicNetworkCategory']>>,
  Assert<Same<GridConnectionStatus, Schemas['PublicGridConnectionStatus']>>,
  Assert<Same<MunicipalApprovalStatus, Schemas['PublicMunicipalApprovalStatus']>>,
  Assert<Same<AvailabilityStatus, Schemas['PublicAvailabilityStatus']>>,
  Assert<Same<IntroductionRole, Schemas['IntroductionRequesterRole']>>,
  Assert<Same<IntroductionTiming, Schemas['IntroductionTiming']>>,
];
